/**
 * Cockpit Ejecutivo · portada de 60 segundos (roadmap 16/10).
 * Titular honesto + 6 indicadores + 3 decisiones + riesgos + tabla de zonas,
 * todo desde /api/cockpit/resumen (misma fuente que YTD/Pipeline/Zonas).
 * Filtro por zona comercial. Cada bloque lleva a su detalle en un clic.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useZona } from "@/context/ZonaContext";
import { MetricInfo } from "@/components/MetricInfo";
import { CockpitDetalleDrawer } from "@/components/CockpitDetalleDrawer";
import {
  Loader2, Gauge, TrendingUp, FileCheck2, Trophy, HelpCircle, MapPinned,
  AlertTriangle, ChevronRight, Building2, ArrowUpRight,
} from "lucide-react";

const ICONS = {
  pipeline_en_curso: TrendingUp, hit_rate: Gauge, presentadas: FileCheck2,
  cartera_firme: Trophy, por_decidir: HelpCircle, cobertura_zona: MapPinned,
};
const NIVEL = {
  alto: { dot: "bg-rose-500", chip: "text-rose-700 bg-rose-50 border-rose-200" },
  medio: { dot: "bg-amber-500", chip: "text-amber-700 bg-amber-50 border-amber-200" },
  bajo: { dot: "bg-slate-400", chip: "text-slate-600 bg-slate-50 border-slate-200" },
};

const fmtMM = (n) => (n == null ? "—" : `${Number(n).toLocaleString("es-ES", { maximumFractionDigits: 0 })} M€`);
const fmtPct = (n) => (n == null ? "N/D" : `${n}%`);
const DG_OPTS = [{ v: "", label: "Ambas DG" }, { v: "SERVICIOS", label: "Urban Services" }, { v: "TRATAMIENTO", label: "Waste Treatment" }];
const YEAR_OPTS = [{ v: "", label: "Todos los años" }, { v: "2026", label: "2026" }, { v: "2025", label: "2025" }];

const IndicadorValue = ({ i }) => {
  if (i.valor_pct !== undefined && i.valor_mm === undefined && i.valor_n === undefined)
    return <span>{fmtPct(i.valor_pct)}</span>;
  if (i.valor_pct !== undefined && i.valor_pct !== null && i.clave === "hit_rate")
    return <span>{fmtPct(i.valor_pct)}<span className="text-sm font-normal text-slate-400 ml-1.5">N={i.valor_n}</span></span>;
  if (i.clave === "hit_rate") return <span>N/D</span>;
  return <span>{fmtMM(i.valor_mm)}{i.valor_n != null && <span className="text-sm font-normal text-slate-400 ml-1.5">{i.valor_n} ops.</span>}</span>;
};

export default function Cockpit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zonasCat, setZonasCat] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const { zonaGlobal, yearGlobal, setYearGlobal, dgGlobal, setDgGlobal } = useZona();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/zonas`).then(({ data }) => setZonasCat([...(data.zonas || []), data.sin_zona])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (yearGlobal) p.set("year", yearGlobal);
      if (dgGlobal) p.set("dg", dgGlobal);
      if (zonaGlobal) p.set("zona", zonaGlobal);
      const { data: d } = await axios.get(`${API}/cockpit/resumen?${p.toString()}`);
      setData(d);
    } catch (e) {
      toast.error("No se pudo cargar el Cockpit");
    } finally {
      setLoading(false);
    }
  }, [yearGlobal, dgGlobal, zonaGlobal]);

  useEffect(() => { load(); }, [load]);

  const zonaLabel = !zonaGlobal ? "Todas las zonas"
    : (zonasCat.find((z) => z.codigo === zonaGlobal)?.nombre_visible || zonaGlobal);
  const dgLabel = DG_OPTS.find((o) => o.v === dgGlobal)?.label || "Ambas DG";

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="cockpit-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Gauge size={24} className="text-indigo-600" /> Cockpit Ejecutivo</h1>
          <p className="text-sm text-slate-500 mt-1">Cómo va el negocio en 60 segundos — misma fuente comercial (ofertas_comercial).</p>
          <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="cockpit-ambito">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Ámbito:</span>
            <span className="text-[11px] font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{yearGlobal || "Todos los años"}</span>
            <span className="text-[11px] font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{dgLabel}</span>
            <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${zonaGlobal ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{zonaLabel}</span>
            <span className="text-[11px] text-slate-400">· la zona se elige en el selector de la cabecera</span>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <select value={yearGlobal} onChange={(e) => setYearGlobal(e.target.value)} data-testid="cockpit-year" className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm">
            {YEAR_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <select value={dgGlobal} onChange={(e) => setDgGlobal(e.target.value)} data-testid="cockpit-dg" className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm">
            {DG_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" size={26} />Preparando portada…</div>
      ) : !data ? null : (
        <>
          {/* Titular */}
          <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white rounded-2xl p-5 shadow-sm" data-testid="cockpit-titular">
            <p className="text-[11px] uppercase tracking-wider text-indigo-200 font-semibold mb-1">Titular · corte {data.corte}</p>
            <p className="text-lg font-semibold leading-snug">{data.titular}</p>
            {data.cobertura?.pct_con_zona != null && (
              <button onClick={() => setDetalle("cobertura_zona")} data-testid="cockpit-cobertura" className="text-xs text-indigo-200 mt-2 hover:text-white hover:underline text-left">
                Cobertura de zona{zonaGlobal ? " (global)" : ""} {data.cobertura.pct_con_zona}% ({data.cobertura.con_zona}/{data.cobertura.total}); {data.cobertura.sin_zona} sin zona asignada.
              </button>
            )}
          </div>

          {/* 6 indicadores */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cockpit-indicadores">
            {data.indicadores.map((i) => {
              const Icon = ICONS[i.clave] || Gauge;
              return (
                <div key={i.clave} role="button" tabIndex={0}
                  onClick={() => setDetalle(i.clave)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetalle(i.clave); } }}
                  data-testid={`cockpit-kpi-${i.clave}`}
                  className="cursor-pointer text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-sm transition group">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                      <Icon size={13} className="text-indigo-500" /> {i.label}
                      {i.info && <span onClick={(e) => e.stopPropagation()}><MetricInfo testId={`cockpit-info-${i.clave}`} {...i.info} /></span>}
                    </span>
                    <ArrowUpRight size={14} className="text-slate-300 group-hover:text-indigo-500" />
                  </div>
                  <p className="text-2xl font-bold text-slate-800 mt-1.5"><IndicadorValue i={i} /></p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Decisiones ahora */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="cockpit-decisiones">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-700">Decisiones ahora</h2>
                <button onClick={() => navigate("/tender360?tab=m0")} className="text-xs text-indigo-600 hover:underline font-semibold" data-testid="cockpit-decisiones-all">Ver las {data.decisiones_total} →</button>
              </div>
              <div className="divide-y divide-slate-100">
                {data.decisiones.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 italic">Nada pendiente de decidir.</p>}
                {data.decisiones.map((d) => (
                  <button key={d.id} onClick={() => navigate(`/ofertas-comercial?tab=list&open=${d.id}`)} data-testid={`cockpit-decision-${d.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{d.nombre}</p>
                      <p className="text-xs text-slate-400 truncate">{d.objeto}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-slate-700">{fmtMM(d.importe_mm)}</p>
                      <ChevronRight size={14} className="text-slate-300 inline" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Riesgos */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="cockpit-riesgos">
              <div className="px-4 py-3 border-b border-slate-100">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" /> Riesgos y avisos</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {data.riesgos.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 italic">Sin avisos relevantes.</p>}
                {data.riesgos.map((r) => (
                  <button key={r.clave} onClick={() => navigate(r.destino)} data-testid={`cockpit-riesgo-${r.clave}`}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${NIVEL[r.nivel]?.dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800">{r.titulo}</p>
                      <p className="text-xs text-slate-400">{r.detalle}</p>
                    </div>
                    <ChevronRight size={14} className="text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tabla de zonas */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="cockpit-zonas">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Building2 size={14} className="text-emerald-600" /> Por zona comercial</h2>
              <button onClick={() => navigate("/resultados-por-zona")} className="text-xs text-indigo-600 hover:underline font-semibold">Detalle →</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left px-4 py-2">Zona</th><th className="text-right px-4 py-2">Operaciones</th><th className="text-right px-4 py-2">Valor</th><th className="text-right px-4 py-2">Firmadas</th><th className="text-right px-4 py-2">Acierto</th></tr>
              </thead>
              <tbody>
                {data.zonas.map((z) => (
                  <tr key={z.codigo} onClick={() => navigate(`/resultados-por-zona`)} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${z.es_sin_zona ? "bg-slate-50/70" : ""}`} data-testid={`cockpit-zona-row-${z.codigo}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-700">
                      <span className={z.es_sin_zona ? "italic text-slate-500" : ""}>{z.nombre_visible}</span>
                      {z.responsable && <span className="text-xs text-slate-400 font-normal ml-1.5">· {z.responsable}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">{z.operaciones}</td>
                    <td className="px-4 py-2.5 text-right">{fmtMM(z.valor_mm)}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-600">{z.firmadas != null ? z.firmadas : z.ganadas}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-sky-600">
                      {fmtPct(z.hit_rate_value)}
                      {z.hit_rate_n != null && z.hit_rate_n > 0 && z.hit_rate_n < 5 && <span className="ml-1 text-[10px] text-amber-600 font-semibold">N={z.hit_rate_n}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <CockpitDetalleDrawer
        metric={detalle}
        scope={{ year: yearGlobal, dg: dgGlobal, zona: zonaGlobal }}
        onClose={() => setDetalle(null)}
      />
    </div>
  );
}
